import { useEffect, useRef, useState } from 'react'
import { X, DollarSign, AlertCircle, Calendar, User as UserIcon, Printer } from 'lucide-react'
import { useGetCashOutStatus, useClockOut, usePrintCashOutReport } from '@/api/auth'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'
import { DropCashModal } from './DropCashModal'

interface CashOutModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCashOutSuccess?: () => void
}

const DENOMINATIONS = [
  { label: '$100', value: 100 },
  { label: '$50', value: 50 },
  { label: '$20', value: 20 },
  { label: '$10', value: 10 },
  { label: '$5', value: 5 },
  { label: '$2', value: 2 },
  { label: '$1', value: 1 },
  { label: '$0.25', value: 0.25 },
  { label: '$0.10', value: 0.10 },
  { label: '$0.05', value: 0.05 },
  { label: '$0.01', value: 0.01 },
]

export function CashOutModal({ open, onOpenChange, onCashOutSuccess }: CashOutModalProps) {
  const { staff } = useSessionStore()
  const { showToast } = useUIStore()
  const { data: cashOutStatus, isLoading } = useGetCashOutStatus(open)
  const clockOut = useClockOut()
  const printCashOut = usePrintCashOutReport()

  const [quantities, setQuantities] = useState<Record<number, number>>({})
  const [varianceNote, setVarianceNote] = useState('')
  const [showDropCash, setShowDropCash] = useState(false)
  const [cardValues, setCardValues] = useState({
    debit: '',
    discover: '',
    visa: '',
    mastercard: '',
    amex: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const printPayloadRef = useRef<Record<string, unknown> | null>(null)

  // Reset submitted flag whenever the modal is closed so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setSubmitted(false)
      printPayloadRef.current = null
    }
  }, [open])

  // Debug breadcrumbs were here for the cash-out drilldown; removed
  // because they printed cash counts and till totals to the production
  // console. If you need to re-enable for diagnosis, gate behind
  // `import.meta.env.DEV`. Network panel still shows the underlying
  // /staff/cash-out request.

  useEffect(() => {
    if (open && cashOutStatus) {
      const fmt = (v: string | number | undefined | null) => {
        const n = parseFloat((v ?? '0').toString())
        return Number.isFinite(n) ? n.toFixed(2) : ''
      }
      const previousCashOut = cashOutStatus.cash_out as { moneys?: Array<{ value: number; qty: number; description: string }> } | null
      const isUpdate = !!previousCashOut
      const isReadOnlyMode = cashOutStatus.editable === false

      // In update mode, pre-fill from prior submission. Otherwise start blank.
      if (isUpdate && previousCashOut?.moneys) {
        const seeded: Record<number, number> = {}
        for (const m of previousCashOut.moneys) {
          if (m.qty > 0) seeded[m.value] = m.qty
        }
        setQuantities(seeded)
      } else {
        setQuantities({})
      }
      setVarianceNote('')

      // Card values: prefer the cashier's previously-entered *_new values
      // when updating, otherwise fall back to the expected card totals.
      const status = cashOutStatus as unknown as Record<string, unknown>
      const pick = (newKey: string, fallbackKey: string) => {
        const newVal = status[newKey]
        if (newVal != null && parseFloat(String(newVal)) !== 0) return fmt(newVal as string | number)
        return fmt(status[fallbackKey] as string | number)
      }
      setCardValues({
        debit: isUpdate ? pick('debit_new', 'debit') : fmt(cashOutStatus.debit),
        discover: isUpdate ? pick('discover_new', 'discover') : fmt((cashOutStatus as unknown as { discover?: string | number }).discover),
        visa: isUpdate ? pick('visa_new', 'visa') : fmt(cashOutStatus.visa),
        mastercard: isUpdate ? pick('master_card_new', 'master_card') : fmt(cashOutStatus.master_card),
        amex: isUpdate ? pick('amex_new', 'amex') : fmt(cashOutStatus.amex),
      })

      // Pre-seed the print payload for read-only views (completed shifts) so
      // the Print button is immediately usable without a fresh submission.
      if (isReadOnlyMode && previousCashOut) {
        const moneys = previousCashOut.moneys ?? []
        const totalInDrawer = moneys.reduce((s, m) => s + m.value * m.qty, 0)
        const startCash = parseFloat(String(status.total_in ?? '0'))
        const saleCash = parseFloat(cashOutStatus.cash || '0')
        const dropCash = parseFloat(cashOutStatus.drop_cash || '0')
        const totalSales = parseFloat(cashOutStatus.total || '0')
        const taxTotal = parseFloat(cashOutStatus.taxes || '0')
        // Match V1's print convention: the printed slip carries the
        // journal-computed EXPECTED card totals, not the cashier's
        // overrides. The *_new values stay persisted on PosClockInOut
        // and are still surfaced in the modal UI, but the paper slip
        // shows what the POS system computed. See clock_out.py
        // L414-488 for the BE side.
        const debit = parseFloat(String(status.debit ?? '0'))
        const discover = parseFloat(String(status.discover ?? '0'))
        const visa = parseFloat(String(status.visa ?? '0'))
        const mastercard = parseFloat(String(status.master_card ?? '0'))
        const amex = parseFloat(String(status.amex ?? '0'))
        const totalCardsForPrint = parseFloat(String(status.total_cards ?? '0'))
        printPayloadRef.current = {
          cash_out: { moneys },
          // Shift-start timestamp from the clock-in JT. The BE's
          // print template renders this as the "From <timestamp>"
          // line on the cash-out slip (see hypnotoad/handlers/api/
          // staff/clock_out.py StaffClockOutPrintHandler._build_print_buffer
          // around L445). Without it the slip prints "From" with no
          // value -- the user-visible "cash out slips aren't printing
          // the date" bug.
          start: (status as unknown as { start?: string | null })?.start ?? null,
          total_out: totalInDrawer,
          total_in: startCash,
          cash: saleCash,
          drop_cash: dropCash,
          variance: totalInDrawer - (startCash + saleCash - dropCash),
          debit,
          discover,
          visa,
          master_card: mastercard,
          amex,
          total_cards: totalCardsForPrint,
          total_before_taxes: totalSales - taxTotal,
          total: totalSales,
          taxes: taxTotal,
        }
      }
    }
  }, [open, cashOutStatus])

  const handleQuantityChange = (value: number, qty: string) => {
    const quantity = parseInt(qty) || 0
    setQuantities(prev => ({
      ...prev,
      [value]: quantity,
    }))
  }

  const handleCardValueChange = (key: keyof typeof cardValues, raw: string) => {
    // Allow only digits with up to 2 decimal places
    let sanitized = raw.replace(/[^0-9.]/g, '')
    const firstDot = sanitized.indexOf('.')
    if (firstDot !== -1) {
      const intPart = sanitized.slice(0, firstDot)
      const decPart = sanitized.slice(firstDot + 1).replace(/\./g, '').slice(0, 2)
      sanitized = `${intPart}.${decPart}`
    }
    setCardValues(prev => ({ ...prev, [key]: sanitized }))
  }

  const calculateTotal = () => {
    return Object.entries(quantities).reduce((sum, [value, qty]) => {
      return sum + (parseFloat(value) * qty)
    }, 0)
  }

  const buildPrintPayload = (): Record<string, unknown> => {
    const total = calculateTotal()
    const moneys = DENOMINATIONS.map(denom => ({
      description: denom.label,
      value: denom.value,
      qty: quantities[denom.value] || 0,
    })).filter(m => m.qty > 0)

    const startCash = parseFloat((cashOutStatus as any)?.total_in || '0')
    const saleCash = parseFloat(cashOutStatus?.cash || '0')
    const dropCash = parseFloat(cashOutStatus?.drop_cash || '0')
    const totalSales = parseFloat(cashOutStatus?.total || '0')
    const taxTotal = parseFloat(cashOutStatus?.taxes || '0')
    const totalBeforeTaxes = totalSales - taxTotal
    const expectedCash = startCash + saleCash - dropCash
    const cashVariance = total - expectedCash

    // The printed slip carries the JOURNAL-COMPUTED EXPECTED card
    // totals -- the system's source of truth -- not the cashier's
    // overrides. This matches the V1 convention: V1's FE serializes
    // the ClockOut viewmodel via ko.mapping.toJS(self), and self.debit
    // is the BE-returned expected. The BE print handler
    // (StaffClockOutPrintHandler in waio-portal clock_out.py L414-488)
    // reads `debit`/`visa`/`master_card`/`amex`/`discover`/`total_cards`
    // and prints them as the line items.
    //
    // The cashier's override values (cardValues.*) are still persisted
    // via the cash-out submit endpoint as `*_new` on PosClockInOut --
    // they remain queryable via the DB for reconciliation -- but they
    // do not appear on the printed receipt, just like in V1. If an
    // expected-vs-entered split needs to land on paper later, that
    // would be a deliberate departure from V1; tracked separately.
    const statusForExpected = cashOutStatus as unknown as Record<string, unknown> | undefined
    const debit = parseFloat(String(statusForExpected?.debit ?? '0'))
    const discover = parseFloat(String(statusForExpected?.discover ?? '0'))
    const visa = parseFloat(String(statusForExpected?.visa ?? '0'))
    const mastercard = parseFloat(String(statusForExpected?.master_card ?? '0'))
    const amex = parseFloat(String(statusForExpected?.amex ?? '0'))
    const totalCardsForPrint = parseFloat(String(statusForExpected?.total_cards ?? '0'))

    return {
      cash_out: { moneys },
      // Shift-start timestamp from the clock-in JT (BE returns it in
      // the cash-out status payload as `start`). The BE print template
      // renders this as the "From <timestamp>" line on the slip; if we
      // don't forward it the slip prints "From" with no value -- the
      // visible "cash-out slips aren't printing the date" bug.
      start: (cashOutStatus as unknown as { start?: string | null })?.start ?? null,
      total_out: total,
      total_in: startCash,
      cash: saleCash,
      drop_cash: dropCash,
      variance: cashVariance,
      debit,
      discover,
      visa,
      master_card: mastercard,
      amex,
      total_cards: totalCardsForPrint,
      total_before_taxes: totalBeforeTaxes,
      total: totalSales,
      taxes: taxTotal,
    }
  }

  const handlePrint = async () => {
    const payload = printPayloadRef.current
    if (!payload) return
    try {
      await printCashOut.mutateAsync(payload)
    } catch {
      showToast({ type: 'error', title: 'Print Failed', message: 'Could not reach the printer. Use the Print button to retry.' })
    }
  }

  const handleSubmit = async () => {
    const total = calculateTotal()
    
    const moneys = DENOMINATIONS.map(denom => ({
      description: denom.label,
      value: denom.value,
      qty: quantities[denom.value] || 0,
    })).filter(m => m.qty > 0)

    const totalCards =
      parseFloat(cardValues.debit || '0') +
      parseFloat(cardValues.discover || '0') +
      parseFloat(cardValues.visa || '0') +
      parseFloat(cardValues.mastercard || '0') +
      parseFloat(cardValues.amex || '0')

    const payload = {
      cashOut: { moneys },
      totalOut: total,
      varianceNote: varianceNote || undefined,
      debitNew: parseFloat(cardValues.debit || '0'),
      discoverNew: parseFloat(cardValues.discover || '0'),
      visaNew: parseFloat(cardValues.visa || '0'),
      masterCardNew: parseFloat(cardValues.mastercard || '0'),
      amexNew: parseFloat(cardValues.amex || '0'),
      totalCardsNew: totalCards,
    }

    // Capture the print payload before the mutation so form state isn't
    // affected by the subsequent cashOutStatus refetch.
    printPayloadRef.current = buildPrintPayload()

    try {
      await clockOut.mutateAsync(payload)
      useSessionStore.getState().clockOut()
      // Signal POSLayout's "cash-out completed here" latch only for the
      // first close of a shift. Update mode edits an existing cash-out and
      // should not suppress future arrival cash-in prompts for this mount.
      if (!hasPreviousCashOut) {
        onCashOutSuccess?.()
      }
      setSubmitted(true)
      // Auto-print; non-blocking so UI updates immediately.
      handlePrint()
    } catch (error) {
      // Failure shape only — payload (cash counts, variance note)
      // intentionally omitted so production consoles don't carry
      // till totals.
      if (import.meta.env.DEV) {
        console.error('[CashOutModal] Failed to clock out:', error)
      }
    }
  }

  if (!open) return null

  const total = calculateTotal()
  const startingCash = parseFloat((cashOutStatus as any)?.total_in || '0')
  const salesCash = parseFloat(cashOutStatus?.cash || '0')
  const dropCash = parseFloat(cashOutStatus?.drop_cash || '0')
  const totalSales = parseFloat(cashOutStatus?.total || '0')
  const taxes = parseFloat(cashOutStatus?.taxes || '0')
  const totalBeforeTaxes = totalSales - taxes
  const expectedCash = startingCash + salesCash - dropCash
  const variance = total - expectedCash
  // Three modes, mirroring the legacy ClockOut.html template:
  //   - editable && !cash_out  → first close of the shift ("Close Drawer Count")
  //   - editable && cash_out   → reopening to amend a prior close ("Update Drawer Count")
  //   - !editable              → read-only view of the closed drawer
  const previousCashOut = cashOutStatus?.cash_out
  const hasPreviousCashOut = !!previousCashOut
  const isReadOnly = cashOutStatus?.editable === false
  const isUpdate = !isReadOnly && hasPreviousCashOut
  const titleText = isReadOnly
    ? 'Read-Only: Last Drawer Count'
    : isUpdate
      ? 'Update Drawer Count'
      : 'Close Drawer Count'
  const previousCashOutTotal = (previousCashOut as any)?.moneys?.reduce((sum: number, m: any) => sum + (m.value * m.qty), 0) || 0
  const totalCardsCounted =
    parseFloat(cardValues.debit || '0') +
    parseFloat(cardValues.discover || '0') +
    parseFloat(cardValues.visa || '0') +
    parseFloat(cardValues.mastercard || '0') +
    parseFloat(cardValues.amex || '0')

  const variantCount = (isReadOnly ? previousCashOutTotal : total) - expectedCash
  const totalCardsExpected = parseFloat(cashOutStatus?.total_cards || '0')
  const totalCardsValue = isReadOnly ? parseFloat((cashOutStatus as any)?.total_cards_new || '0') : totalCardsCounted
  // Card variance mirrors cash variance: BE already persists *_new
  // separately from the journal-computed expected (PosClockInOut model
  // in waio-portal). We just surface the delta here so a cashier
  // override can't slip through without a note. See clock_out.py L309-314.
  const cardVariance = totalCardsValue - totalCardsExpected
  const hasCashVariance = Math.abs(variance) >= 0.01
  const hasCardVariance = Math.abs(cardVariance) >= 0.01
  const hasAnyVariance = hasCashVariance || hasCardVariance

  const personnelOutName = (cashOutStatus as any)?.personnel_out_full_name as string | undefined
  // Prefer the BE-supplied personnel_in.full_name (the cashier who
  // opened the shift being closed) over sessionStore.staff.full_name.
  // sessionStore.staff carries the auth-payload `full_name`, which is
  // the login username ("jeremy") rather than the proper display name
  // -- the BE has the correct value and ships it on the cash-out
  // status payload. Same pattern as CashInModal's personnelInName.
  const personnelInName =
    (cashOutStatus as any)?.personnel_in?.full_name as string | undefined
    ?? staff?.full_name
  const finishDate = cashOutStatus?.finish ? new Date(cashOutStatus.finish) : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <DollarSign className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-bold text-gray-900 leading-tight">
                {titleText}
              </h2>
            </div>
            <div className="flex items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {(hasPreviousCashOut && finishDate) ? finishDate.toLocaleString() : new Date().toLocaleString()}
              </span>
              {personnelInName && (
                <span className="flex items-center gap-1">
                  <UserIcon size={12} />
                  {personnelInName}
                </span>
              )}
              {hasPreviousCashOut && finishDate && (
                <span className="text-gray-400">
                  Closed by {personnelOutName || personnelInName || '—'} at {finishDate.toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-gray-400">
              <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin mr-2" />
              Loading shift details…
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 items-start">
              {/* COL 1: Cash Count */}
              <Panel title="Count Cash">
                <div className="space-y-1">
                  {DENOMINATIONS.map((denom) => {
                    const qty = isReadOnly
                      ? ((previousCashOut as any)?.moneys?.find((m: any) => m.value === denom.value)?.qty || 0)
                      : (quantities[denom.value] || '')
                    const lineTotal = isReadOnly
                      ? (qty as number) * denom.value
                      : ((quantities[denom.value] || 0) * denom.value)
                    return (
                      <div key={denom.value} className="flex items-center gap-2">
                        {isReadOnly ? (
                          <div className="w-16 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-center text-gray-700 text-sm tabular-nums">
                            {qty}
                          </div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={qty}
                            onChange={(e) => handleQuantityChange(denom.value, e.target.value)}
                            className="w-16 px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-red-400 focus:ring-0 outline-none text-center text-sm tabular-nums"
                            placeholder="0"
                          />
                        )}
                        <span className="text-gray-400 text-sm">×</span>
                        <div className="w-12 text-right font-medium text-gray-600 text-sm tabular-nums">
                          {denom.label}
                        </div>
                        <span className="text-gray-300 text-sm flex-1 text-right">=</span>
                        <div className="w-20 text-right text-sm text-gray-900 tabular-nums">
                          ${lineTotal.toFixed(2)}
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">Total in drawer</span>
                  <span className="text-sm font-bold text-gray-900 tabular-nums">
                    ${(isReadOnly ? previousCashOutTotal : total).toFixed(2)}
                  </span>
                </div>

                {!isReadOnly && !hasPreviousCashOut && (
                  <button
                    onClick={() => setShowDropCash(true)}
                    className="mt-3 w-full px-3 py-2 text-sm font-medium bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors cursor-pointer"
                  >
                    Drop Cash
                  </button>
                )}
              </Panel>

              {/* COL 2: Shift Summary
                  The panel is one column visually but holds three
                  separate decompositions of the day's activity:
                    1. Cash         — drawer reconciliation
                    2. Cards        — expected card-machine totals
                    3. Sales total  — pre-tax / tax / total view of the
                                       same money that's already in (1)+(2)
                  Without explicit sub-section labels the layout reads
                  like one continuous math sequence, which led cashiers
                  to think "Subtotal $X under Total cards" was claiming
                  the cards subtotal, when it's actually a different
                  view of the whole day's sales. Sub-section labels
                  make each block self-identifying. */}
              <Panel title="Shift Summary">
                <SubSectionHeader first>Cash</SubSectionHeader>
                <SummaryRow label="Starting cash" value={`$${startingCash.toFixed(2)}`} />
                {/* BE returns total_in from the *current clerk's* last
                    cash-in JT price when SCF pos.cashout_by_clerk is
                    ON (clock_common.py L387-388 keeps personnel_id in
                    the filter). So a $0 here means this clerk hasn't
                    clocked in for this shift -- not that "the float
                    wasn't counted in general." Mirrors V1's framing
                    on index_2.html L213: "You're the first person to
                    clock in today." The POSLayout auto-prompt should
                    have caught this earlier; this hint is a safety
                    net for the dismissed-and-continued path. */}
                {!isReadOnly && startingCash < 0.01 && (
                  <p className="text-[10px] text-amber-700 -mt-0.5">
                    You haven't clocked in for this shift — any float in the drawer will register as variance.
                  </p>
                )}
                <SummaryRow label="Sales cash" value={`$${salesCash.toFixed(2)}`} />
                <SummaryRow label="Drop cash" value={`$${dropCash.toFixed(2)}`} />
                <SummaryRow
                  label="Cash Variance"
                  value={`${variantCount >= 0 ? '+' : '-'}$${Math.abs(variantCount).toFixed(2)}`}
                  valueClass={Math.abs(variantCount) < 0.01 ? 'text-green-600' : 'text-red-600'}
                />

                <SubSectionHeader>Cards</SubSectionHeader>
                <SummaryRow label="Debit" value={`$${parseFloat(cashOutStatus?.debit || '0').toFixed(2)}`} />
                <SummaryRow label="Discover" value={`$${parseFloat((cashOutStatus as unknown as { discover?: string | number })?.discover ? String((cashOutStatus as unknown as { discover?: string | number }).discover) : '0').toFixed(2)}`} />
                <SummaryRow label="Visa" value={`$${parseFloat(cashOutStatus?.visa || '0').toFixed(2)}`} />
                <SummaryRow label="Mastercard" value={`$${parseFloat(cashOutStatus?.master_card || '0').toFixed(2)}`} />
                <SummaryRow label="AMEX" value={`$${parseFloat(cashOutStatus?.amex || '0').toFixed(2)}`} />
                <SummaryRow label="Total cards" value={`$${totalCardsExpected.toFixed(2)}`} bold />

                <SubSectionHeader>Sales Total</SubSectionHeader>
                <SummaryRow label="Subtotal" value={`$${totalBeforeTaxes.toFixed(2)}`} />
                <SummaryRow label="GST" value={`$${taxes.toFixed(2)}`} />
                <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-gray-200">
                  <span className="text-gray-700 font-semibold text-sm">Total</span>
                  <span className="text-gray-900 font-bold text-base tabular-nums">${totalSales.toFixed(2)}</span>
                </div>
              </Panel>

              {/* COL 3: Verify Credit/Debit Values + variance note */}
              <div className="space-y-4">
                <Panel title="Verify Credit/Debit Values">
                  <CardInputRow
                    label="Debit"
                    value={cardValues.debit}
                    onChange={(v) => handleCardValueChange('debit', v)}
                    expected={parseFloat(cashOutStatus?.debit || '0')}
                    readOnly={isReadOnly}
                    readOnlyValue={parseFloat((cashOutStatus as any)?.debit_new || '0')}
                  />
                  <CardInputRow
                    label="Discover"
                    value={cardValues.discover}
                    onChange={(v) => handleCardValueChange('discover', v)}
                    expected={parseFloat((cashOutStatus as any)?.discover || '0')}
                    readOnly={isReadOnly}
                    readOnlyValue={parseFloat((cashOutStatus as any)?.discover_new || '0')}
                  />
                  <CardInputRow
                    label="Visa"
                    value={cardValues.visa}
                    onChange={(v) => handleCardValueChange('visa', v)}
                    expected={parseFloat(cashOutStatus?.visa || '0')}
                    readOnly={isReadOnly}
                    readOnlyValue={parseFloat((cashOutStatus as any)?.visa_new || '0')}
                  />
                  <CardInputRow
                    label="Mastercard"
                    value={cardValues.mastercard}
                    onChange={(v) => handleCardValueChange('mastercard', v)}
                    expected={parseFloat(cashOutStatus?.master_card || '0')}
                    readOnly={isReadOnly}
                    readOnlyValue={parseFloat((cashOutStatus as any)?.master_card_new || '0')}
                  />
                  <CardInputRow
                    label="AMEX"
                    value={cardValues.amex}
                    onChange={(v) => handleCardValueChange('amex', v)}
                    expected={parseFloat(cashOutStatus?.amex || '0')}
                    readOnly={isReadOnly}
                    readOnlyValue={parseFloat((cashOutStatus as any)?.amex_new || '0')}
                  />
                  <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200">
                    <span className="text-sm font-semibold text-gray-700">Total Cards</span>
                    <div className="text-right">
                      <div className={`text-sm font-bold tabular-nums ${hasCardVariance ? 'text-amber-700' : 'text-gray-900'}`}>
                        ${totalCardsValue.toFixed(2)}
                      </div>
                      {hasCardVariance && (
                        <div className="text-[10px] text-amber-700 font-semibold tabular-nums">
                          Expected: ${totalCardsExpected.toFixed(2)} ({cardVariance >= 0 ? '+' : '−'}${Math.abs(cardVariance).toFixed(2)})
                        </div>
                      )}
                    </div>
                  </div>
                </Panel>

                {!isReadOnly && hasAnyVariance && (
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        {hasCashVariance && hasCardVariance
                          ? 'Cash and card variance detected. Add a note explaining the difference.'
                          : hasCashVariance
                            ? 'Cash variance detected. Add a note explaining the difference.'
                            : 'Card variance detected. Add a note explaining the difference.'}
                      </p>
                    </div>
                    <textarea
                      value={varianceNote}
                      onChange={(e) => setVarianceNote(e.target.value)}
                      placeholder="Explain the variance..."
                      className="w-full px-3 py-2 text-sm bg-white border border-amber-200 rounded-lg focus:border-amber-400 focus:ring-0 outline-none resize-none"
                      rows={2}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4 bg-white flex items-center justify-between gap-2">
          <button
            onClick={() => onOpenChange(false)}
            disabled={clockOut.isPending}
            className="px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitted || isReadOnly ? 'Close' : 'Cancel'}
          </button>
          {submitted || isReadOnly ? (
            <button
              onClick={handlePrint}
              disabled={printCashOut.isPending || !printPayloadRef.current}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Printer size={16} />
              {printCashOut.isPending ? 'Printing…' : 'Print'}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={clockOut.isPending || (hasAnyVariance && !varianceNote.trim())}
              className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {clockOut.isPending ? 'Processing…' : isUpdate ? 'Update' : 'Cash Out'}
            </button>
          )}
        </div>
      </div>

      {/* Drop Cash Modal */}
      <DropCashModal
        open={showDropCash}
        onOpenChange={setShowDropCash}
      />
    </div>
  )
}

function SubSectionHeader({ children, first = false }: { children: React.ReactNode; first?: boolean }) {
  // One step quieter than the Panel title above it: smaller text,
  // lighter grey. Visually re-groups rows into labeled blocks without
  // competing with the panel header. Non-first headers get a top HR
  // so the section boundary is unmistakable -- the label alone wasn't
  // a strong enough visual break.
  if (first) {
    return (
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
        {children}
      </p>
    )
  }
  return (
    <>
      <div className="border-t border-gray-200 mt-3 mb-2" />
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
        {children}
      </p>
    </>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">
        {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}

function SummaryRow({ label, value, valueClass = 'text-gray-900', bold = false }: {
  label: string
  value: string
  valueClass?: string
  bold?: boolean
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm tabular-nums ${bold ? 'font-bold' : 'font-medium'} ${valueClass}`}>
        {value}
      </span>
    </div>
  )
}

function CardInputRow({ label, value, onChange, expected, readOnly, readOnlyValue }: {
  label: string
  value: string
  onChange: (v: string) => void
  expected: number
  readOnly: boolean
  readOnlyValue: number
}) {
  const current = readOnly ? readOnlyValue : parseFloat(value || '0')
  const matches = Math.abs(current - expected) < 0.005
  // When the cashier overrides the journal-computed expected, make it
  // obvious: amber-tinted input + bold expected callout. The combined
  // variance message + required note is rendered by the parent.
  const inputClass = matches
    ? 'w-24 px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-red-400 focus:ring-0 outline-none text-right text-sm tabular-nums'
    : 'w-24 px-2 py-1.5 bg-amber-50 border border-amber-400 rounded-lg focus:border-amber-500 focus:ring-0 outline-none text-right text-sm tabular-nums text-amber-900'
  const readOnlyClass = matches
    ? 'w-24 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-right text-sm text-gray-700 tabular-nums'
    : 'w-24 px-2 py-1.5 bg-amber-50 border border-amber-400 rounded-lg text-right text-sm text-amber-800 tabular-nums'
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <div className="flex items-center gap-2">
        {!matches && (
          <span className="text-[10px] text-amber-700 font-semibold tabular-nums">
            exp ${expected.toFixed(2)}
          </span>
        )}
        {readOnly ? (
          <div className={readOnlyClass}>
            ${readOnlyValue.toFixed(2)}
          </div>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
            placeholder="0.00"
          />
        )}
      </div>
    </div>
  )
}
